import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Phone, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Shared form fields for the strategy funnel: country-aware phone input and a
// timezone picker. Phone auto-selects the dialing code from the visitor's
// timezone/locale and always submits a full E.164 number; the timezone picker
// defaults to the browser's zone and re-renders slot times when changed.

const COUNTRIES = [
  { iso: "US", name: "United States", dial: "+1" },
  { iso: "CA", name: "Canada", dial: "+1" },
  { iso: "GB", name: "United Kingdom", dial: "+44" },
  { iso: "AU", name: "Australia", dial: "+61" },
  { iso: "NZ", name: "New Zealand", dial: "+64" },
  { iso: "IE", name: "Ireland", dial: "+353" },
  { iso: "DE", name: "Germany", dial: "+49" },
  { iso: "FR", name: "France", dial: "+33" },
  { iso: "ES", name: "Spain", dial: "+34" },
  { iso: "IT", name: "Italy", dial: "+39" },
  { iso: "NL", name: "Netherlands", dial: "+31" },
  { iso: "BE", name: "Belgium", dial: "+32" },
  { iso: "CH", name: "Switzerland", dial: "+41" },
  { iso: "AT", name: "Austria", dial: "+43" },
  { iso: "SE", name: "Sweden", dial: "+46" },
  { iso: "NO", name: "Norway", dial: "+47" },
  { iso: "DK", name: "Denmark", dial: "+45" },
  { iso: "FI", name: "Finland", dial: "+358" },
  { iso: "PT", name: "Portugal", dial: "+351" },
  { iso: "ZA", name: "South Africa", dial: "+27" },
  { iso: "AE", name: "UAE", dial: "+971" },
  { iso: "SA", name: "Saudi Arabia", dial: "+966" },
  { iso: "IN", name: "India", dial: "+91" },
  { iso: "SG", name: "Singapore", dial: "+65" },
  { iso: "MY", name: "Malaysia", dial: "+60" },
  { iso: "PH", name: "Philippines", dial: "+63" },
  { iso: "HK", name: "Hong Kong", dial: "+852" },
  { iso: "JP", name: "Japan", dial: "+81" },
  { iso: "KR", name: "South Korea", dial: "+82" },
  { iso: "MX", name: "Mexico", dial: "+52" },
  { iso: "BR", name: "Brazil", dial: "+55" },
  { iso: "AR", name: "Argentina", dial: "+54" },
  { iso: "CL", name: "Chile", dial: "+56" },
  { iso: "CO", name: "Colombia", dial: "+57" },
];

// IANA zone -> ISO country for the common zones our visitors hit. Prefix rules
// below cover the rest (all of Australia/*, Pacific/Auckland, etc.).
const TZ_COUNTRY = {
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
  "America/Los_Angeles": "US", "America/Phoenix": "US", "America/Anchorage": "US",
  "America/Boise": "US", "America/Detroit": "US", "America/Indiana/Indianapolis": "US",
  "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Winnipeg": "CA",
  "America/Edmonton": "CA", "America/Halifax": "CA", "America/Montreal": "CA",
  "America/St_Johns": "CA",
  "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Berlin": "DE",
  "Europe/Paris": "FR", "Europe/Madrid": "ES", "Europe/Rome": "IT",
  "Europe/Amsterdam": "NL", "Europe/Brussels": "BE", "Europe/Zurich": "CH",
  "Europe/Vienna": "AT", "Europe/Stockholm": "SE", "Europe/Oslo": "NO",
  "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI", "Europe/Lisbon": "PT",
  "Pacific/Auckland": "NZ", "Africa/Johannesburg": "ZA", "Asia/Dubai": "AE",
  "Asia/Riyadh": "SA", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "Asia/Singapore": "SG", "Asia/Kuala_Lumpur": "MY", "Asia/Manila": "PH",
  "Asia/Hong_Kong": "HK", "Asia/Tokyo": "JP", "Asia/Seoul": "KR",
  "America/Mexico_City": "MX", "America/Sao_Paulo": "BR", "America/Argentina/Buenos_Aires": "AR",
  "America/Santiago": "CL", "America/Bogota": "CO",
};

// People type their FULL name into a field labeled just "Name" (that's the
// point — "First name" was making some type ONLY a first name while others
// typed their whole name into it, landing the whole string in GHL's
// firstName field with no lastName). Split on the first space so GHL's
// first/last fields populate correctly either way.
export function splitName(full) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] || "", last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}


export function guessCountry(tz) {
  let iso = TZ_COUNTRY[tz] || "";
  if (!iso && tz && tz.startsWith("Australia/")) iso = "AU";
  if (!iso) {
    const region = (navigator.language || "").split("-")[1] || "";
    if (COUNTRIES.some((c) => c.iso === region.toUpperCase())) iso = region.toUpperCase();
  }
  return COUNTRIES.find((c) => c.iso === iso) || COUNTRIES[0]; // default US
}

export function toE164(dial, raw) {
  let d = (raw || "").replace(/\D/g, "");
  const dd = (dial || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (!d) return "";
  if (d.startsWith(dd) && d.length > dd.length + 5) return `+${d}`;
  d = d.replace(/^0+/, "");
  return `+${dd}${d}`;
}

// Live dash formatting as the visitor types: 5551234567 -> 555-123-4567 (US/CA),
// 0412345678 -> 0412-345-678 (AU/NZ), etc. Display-only; toE164 strips it back out.
export function formatPhone(dial, raw) {
  let d = (raw || "").replace(/\D/g, "");
  const dd = (dial || "").replace(/\D/g, "");
  if (dd && d.startsWith(dd) && d.length > dd.length + 5) d = d.slice(dd.length); // pasted full intl number
  const group = (sizes) => {
    const max = sizes.reduce((a, b) => a + b, 0);
    d = d.slice(0, max); // cap at the format's full length — no runaway trailing digits
    const out = [];
    let i = 0;
    for (const s of sizes) { if (i >= d.length) break; out.push(d.slice(i, i + s)); i += s; }
    return out.filter(Boolean).join("-");
  };
  if (dd === "1") return group([3, 3, 4]);
  if (dd === "61" || dd === "64") return group([4, 3, 3]);
  if (dd === "44") return group([4, 3, 4]);
  return group([3, 3, 4]);
}

export function PhoneField({ testId, dial, number, onDial, onNumber, className = "", inputClass = "" }) {
  return (
    <div className={`flex gap-2 ${className}`} data-testid={`${testId}-wrap`}>
      <select
        data-testid={`${testId}-country`}
        value={dial}
        onChange={(e) => onDial(e.target.value)}
        aria-label="Country code"
        className={`w-[118px] shrink-0 rounded-xl border border-navy/12 bg-white px-2.5 py-3 text-[14px] font-semibold text-navy/80 outline-none focus:border-brand-indigo/50 ${inputClass}`}
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.dial}>{c.iso} {c.dial}</option>
        ))}
      </select>
      <input
        data-testid={testId}
        placeholder="Cell phone"
        type="tel"
        value={number}
        onChange={(e) => onNumber(formatPhone(dial, e.target.value))}
        className={`w-full rounded-xl border border-navy/12 bg-white px-4 py-3 text-[15px] outline-none transition-colors focus:border-brand-indigo/50 ${inputClass}`}
      />
    </div>
  );
}

// US-only picker (owner call, Sep 2026 — the funnel serves US contractors; a
// worldwide list just invited mis-picks). Arizona + Puerto Rico included since
// they don't follow DST like their neighbors.
const FALLBACK_TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "America/Puerto_Rico"];

export const ALL_TZS = FALLBACK_TZS;

// Browser-guessed timezone -> nearest valid picker option (non-US visitors get Eastern).
export const clampUsTz = (tz) => (ALL_TZS.includes(tz) ? tz : "America/New_York");

export function tzLabel(tz) {
  try {
    const parts = new Intl.DateTimeFormat([], { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts();
    const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
    return `${tz.replace(/_/g, " ")} (${off})`;
  } catch { return tz; }
}

export function TimezoneSelect({ testId, value, onChange }) {
  return (
    <label className="mt-3 flex items-center gap-2 text-[12px] font-semibold text-navy/45" data-testid={`${testId}-wrap`}>
      Times shown in
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Your timezone"
        className="max-w-full rounded-lg border border-navy/10 bg-white px-2 py-1.5 text-[12px] font-semibold text-navy/70 outline-none focus:border-brand-indigo/50"
      >
        {ALL_TZS.map((z) => <option key={z} value={z}>{tzLabel(z)}</option>)}
      </select>
    </label>
  );
}

// "..., City, ST 78748[, United States]" -> "City, ST" so picking a suggestion can fill the city box.
export function cityFromAddress(address) {
  let parts = (address || "").split(",").map((s) => s.trim()).filter(Boolean);
  // Drop a trailing country segment ("..., United States") so ST/ZIP is last.
  while (parts.length && /^(united states|usa|u\.?s\.?a?\.?)$/i.test(parts[parts.length - 1])) parts.pop();
  if (parts.length < 2) return "";
  const m = parts[parts.length - 1].match(/\b([A-Za-z]{2})\b/);
  const state = m ? m[1].toUpperCase() : "";
  return state ? `${parts[parts.length - 2]}, ${state}` : parts[parts.length - 2];
}

// Business-name autocomplete backed by real Google Maps listings. Typing
// (debounced) shows matching businesses with their address; clicking one locks
// the exact place (place_id) and can auto-fill the city. If nothing matches,
// the dropdown says so and the plain manual fields still work.
export function BusinessLookupField({ testId, value, onChange, onPick, city = "", wide = false,
                                      placeholder = "e.g. Hale & Monroe Law", inputClass = "", className = "" }) {
  const [results, setResults] = useState(null); // null = idle, [] = searched, no hits
  const [unavail, setUnavail] = useState(false); // provider down/quota — not a "no match"
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const lastQ = useRef("");

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  // city anchors the search — a new city means fresh suggestions
  useEffect(() => { lastQ.current = ""; setResults(null); setOpen(false); }, [city]);

  const lookup = (q) => {
    clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 3) { setResults(null); setOpen(false); return; }
    if (query.toLowerCase() === lastQ.current && results !== null) { setOpen(true); return; } // same query — reuse, save a search
    timer.current = setTimeout(async () => {
      setBusy(true);
      setOpen(true); // show the "searching" row while Google answers
      lastQ.current = query.toLowerCase();
      try {
        const base = `${API}/audit/places?q=${encodeURIComponent(query)}&city=${encodeURIComponent(city.trim())}`;
        let d = await fetch(wide ? `${base}&wide=1` : base).then((x) => x.json()).catch(() => ({}));
        let places = Array.isArray(d.places) ? d.places : [];
        // Nothing nearby? Retry nationwide (drops the city/IP geo-anchor) so a
        // business in another area still surfaces, just like Google Search.
        if (places.length === 0 && !d.unavailable && !wide) {
          const dw = await fetch(`${base}&wide=1`).then((x) => x.json()).catch(() => ({}));
          if (Array.isArray(dw.places) && dw.places.length) { places = dw.places; d = dw; }
        }
        setResults(places);
        setUnavail(!!d.unavailable);
        setOpen(true);
      } catch { setResults([]); setUnavail(true); setOpen(true); }
      setBusy(false);
    }, 600);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      {/* spinner is pinned to THIS wrapper (input-only) — the dropdown below is
          in-flow, so anchoring to the outer box let the spinner drift out of
          the bar once suggestions opened */}
      <div className="relative">
        <input data-testid={testId} value={value} placeholder={placeholder} autoComplete="off"
          onChange={(e) => { onChange(e.target.value); lookup(e.target.value); }}
          onFocus={() => { if (results?.length) setOpen(true); }}
          className={inputClass} />
        {busy && <span data-testid={`${testId}-spinner`} className="sl-ring-spin pointer-events-none absolute right-4 top-1/2 -mt-[9px] h-[18px] w-[18px] rounded-full" />}
      </div>
      {open && (busy || results) && (
        /* in-flow (not absolute) so the list pushes content down and can never
           cover the Continue button on small screens; capped + scrollable */
        <div data-testid={`${testId}-suggestions`} className="mt-1.5 overflow-hidden rounded-xl border border-navy/10 bg-white shadow-[0_18px_50px_-12px_rgba(14,18,48,0.25)]">
          <div className="max-h-[248px] overflow-y-auto overscroll-contain">
          {busy && !results ? (
            <p data-testid={`${testId}-searching`} className="flex items-center gap-2.5 px-4 py-3 text-[13px] font-semibold text-navy/55">
              <span className="sl-ring-spin h-4 w-4 shrink-0 rounded-full" /> {wide ? "Searching Google Maps…" : "Searching Google Maps near you…"}
            </p>
          ) : results && results.length > 0 ? results.map((p, i) => (
            <button type="button" key={p.place_id || i} data-testid={`${testId}-suggestion-${i}`}
              onClick={() => { onPick?.(p); setOpen(false); setResults(null); }}
              className="flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-brand-indigo/5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-indigo" />
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-bold text-navy">{p.name}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-semibold" data-testid={`${testId}-suggestion-meta-${i}`}>
                  {p.type && <span className="text-navy/50">{p.type}</span>}
                  {p.rating ? (
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {p.rating} rating ({p.reviews})
                    </span>
                  ) : (
                    <span className="text-navy/35">No rating yet</span>
                  )}
                </span>
                {p.address && <span className="block truncate text-[12px] text-navy/50">{p.address}</span>}
                {p.phone && (
                  <span className="mt-0.5 flex items-center gap-1.5 text-[12px] font-semibold text-navy/60" data-testid={`${testId}-suggestion-phone-${i}`}>
                    <Phone className="h-3 w-3 shrink-0 text-brand-indigo/60" />{p.phone}
                  </span>
                )}
              </span>
            </button>
          )) : unavail ? (
            <p data-testid={`${testId}-unavailable`} className="px-4 py-3 text-[13px] font-semibold leading-snug text-navy/55">
              Business search is taking a quick break on our end — no worries. Fill in your details manually below, everything still works the same.
            </p>
          ) : (
            <p data-testid={`${testId}-none`} className="px-4 py-3 text-[13px] font-semibold leading-snug text-navy/55">
              Can't find it on Google — no worries. Fill in the rest manually and we'll scan your area anyway.
            </p>
          )}
          </div>
          {(results?.length ?? 0) > 0 && (
            <p className="border-t border-navy/6 px-4 py-2 text-[11.5px] font-semibold text-navy/40">
              Not seeing it? Keep typing — or just enter your details manually, we'll scan the area either way.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
